import React, { FC, memo, ReactElement, useCallback, useEffect, useMemo, useRef } from 'react';

import './FilterSwiper.less';
import { useMatchMedia } from '../turboLive/hooks/useMatchMedia';
import useArrows from './useArrows';
import clsx from 'clsx';
import { NavLink } from 'react-router-dom';
import { nuiCtx } from '../../common/initialization';
import { NikeRoute, RouteParams } from '../../common/routing/types';

export const FilterSwiper: FC<{
    buttons?: Array<ReactElement>;
    staticButton?: React.ReactElement;
    customCss?: string;
    inline?: boolean;
    refresh: any;
}> = memo(function FilterSwiper({ buttons, staticButton, customCss, inline, refresh }) {
    const matchMedia = useMatchMedia();
    const scrollableNav = useRef(null);
    const parentRef = useRef(null);
    const [arrowLeft, arrowRight, setScrollLeft] = useArrows(scrollableNav, parentRef, refresh);
    const nav = scrollableNav.current;
    const setActiveItemVisible = useCallback(
        item => {
            const offsetLeftWidth = item && item.offsetLeft + item.offsetWidth;
            if (nav && offsetLeftWidth - nav.scrollLeft > nav.offsetWidth) {
                setScrollLeft((nav.scrollLeft = offsetLeftWidth - nav.offsetWidth + 150));
            } else if (nav && nav.scrollLeft > item.offsetLeft) {
                setScrollLeft((nav.scrollLeft = item.offsetLeft - 150));
            }
        },
        [nav, setScrollLeft]
    );
    const buttonElements = useMemo(
        () => buttons.map(b => React.cloneElement(b, { activationChange: setActiveItemVisible })),
        [setActiveItemVisible, buttons]
    );
    return (
        <div
            ref={parentRef}
            className={clsx('filter-swiper flex p-rel', customCss || 'filter-swiper-bottom-shadow', {
                'filter-2-row': !matchMedia.xl && !inline
            })}
        >
            {staticButton}
            {arrowLeft}
            <nav className="filter-swiper-overflow flex p-rel full-width" ref={scrollableNav}>
                {buttonElements}
                <span className="filter-item filter-bgr flex-1" />
            </nav>
            {arrowRight}
        </div>
    );
});

export type FilterButtonProps = {
    activationChange?: (HTMLElement) => void;
};

type NavigationFilterButtonProps = {
    title: string;
    path: string;
    route?: NikeRoute;
    routeParams?: RouteParams;
    icon: React.ReactElement;
    active: boolean;
    activeBg?: string;
    dataAtid?: string;
    badge?: string;
    onClick?: React.MouseEventHandler<HTMLElement>;
} & FilterButtonProps;

export const NavigationFilterButton = ({
    title,
    path,
    route,
    routeParams,
    badge,
    icon,
    onClick = () => {},
    active,
    dataAtid = 'tl-menu-sport',
    activationChange
}: NavigationFilterButtonProps) => {
    const ref = useRef<HTMLAnchorElement>(null);
    useEffect(() => {
        if (activationChange && active && ref.current) {
            activationChange(ref.current);
        }
    }, [active, activationChange]);
    const matchMedia = useMatchMedia();
    const large = matchMedia.xl;
    const isMobileView = nuiCtx.platform === 'mobile';

    return (
        <NavLink
            onClick={e => {
                onClick(e);
                if (route) {
                    e.preventDefault();
                    e.stopPropagation();
                    nuiCtx.routing.navigate(route, routeParams);
                }
            }}
            to={path}
            exact
            data-atid={dataAtid}
            className={clsx('filter-item filter-bgr p-rel ', {
                'flex-col': !large || isMobileView,
                'filter-item-bgr filter-item-active': active,
                'flex-center filter-item-pad': !isMobileView,
                'flex items-center filter-item-mini px-2 pt-15': isMobileView
            })}
            title={title}
            ref={ref}
        >
            <span className={clsx('fs-0', { 'pr-10': large && !isMobileView })}>{icon}</span>
            <span
                className={clsx(`filter-sport ellipsis c-default`, {
                    'fs-14': !isMobileView,
                    'fs-10 py-5': isMobileView
                })}
            >
                {title}{' '}
            </span>
            {badge && (
                <span
                    className={`p-abs fs-10 ${isMobileView ? 'filter-badge-mini' : 'filter-badge'}`}
                    data-atid="match-count-badge"
                >
                    {badge}
                </span>
            )}
        </NavLink>
    );
};
